import { ActionIcon, Badge, Button, Group, Image, Modal, ScrollArea, Stack, Text, useMantineTheme } from '@mantine/core';
import { IconArticle, IconAugmentedReality, IconExternalLink, IconX } from '@tabler/icons-react';
import React, { useState } from 'react'
import { SnapAR } from '../../pages';
import { GitHub } from '@mui/icons-material';
import { useMediaQuery } from '@mantine/hooks';
import { IconPaperclip, IconFileTypePdf, IconBrandYoutube } from '@tabler/icons-react';

function GalleryModal({ key, item, lensID, opened, onClose, image, video, title, date, citations, description, categories, projectLink }) {
    const theme = useMantineTheme();
    const [startAR, setStartAR] = useState(false);
    const isMobile = useMediaQuery("(max-width: 50rem)");

    const [session, setSession] = useState(null);
    const [mediaStream, setMediaStream] = useState(null);

    const handleSession = (session, mediaStream) => {
        if (session) {
            setSession(session);
        }
        if (mediaStream) {
            setMediaStream(mediaStream);
        }
    }

    const handleOnClose = () => {
        onClose();
        if (session) {
            session.pause(); session.removeLens(); session.destroy(); setSession(null);
        }// pause the session
        if (mediaStream) {
            mediaStream.getVideoTracks().forEach(track => track.stop()); setMediaStream(null);
        } // stop all tracks
        setStartAR(false);
    }

    if (opened && item) return (
        <Modal
            opened={opened}
            onClose={handleOnClose}
            withCloseButton={isMobile}
            size="xl"
            radius={isMobile ? 'xs' : 'lg'}
            scrollAreaComponent={ScrollArea.Autosize}
            centered
            // fullScreen={isMobile}
            transitionProps={{ transition: 'fade', duration: 200 }}
            overlayProps={{
                color: theme.colorScheme === 'dark' ? theme.colors.dark[9] : theme.colors.gray[7],
                opacity: 0.55,
                blur: 3,
            }}
            zIndex={0.8}
        >
            {/* close */}
            {!isMobile &&
                <Group position='right'>
                    <ActionIcon onClick={handleOnClose} radius='lg' size='xs'>
                        <IconX />
                    </ActionIcon>
                </Group>
            }

            <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'center',
                alignItems: isMobile ? 'center' : 'flex-start',
                padding: isMobile ? '0' : '0 0.8rem 0.8rem 0.8rem',
                gap: '1rem',
            }}>
                <div id='canvas' style={{
                    flex: 1,
                    maxWidth: '320px',
                    maxHeight: '570px',
                    marginRight: isMobile ? '0' : '1rem',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    borderRadius: '0.5rem',
                    boxShadow: '0 0 0.5rem 0 rgba(0, 0, 0, 0.2)',
                }}>
                    <Image
                        withPlaceholder
                        src={video ? video : image}
                        alt={title}
                        key={key}
                        radius="md"
                        height={570}
                        width={320}
                        sx={{
                            borderRadius: '0.5rem',
                            boxShadow: '0 0 0.5rem 0 rgba(0, 0, 0, 0.2)',
                        }}
                    />

                    {item.win &&
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                        }}>
                            <div>
                                <img src={`/textures/winner_${item.win.position}.png`} alt='gallery' key={key + 'win'} width={150} height={150} />
                            </div>
                        </div>
                    }

                    {/* start AR if lens id is present and button is clicked */}
                    {lensID && startAR && (
                        <>
                            <SnapAR lensID={lensID} opened={opened} startAR={startAR} handleSessionExport={handleSession} />
                        </>
                    )}
                </div>

                {/* add select camers if mobile device */}
                {lensID && isMobile ?
                    <Group>
                        <Text size='md' weight={500} color='dimmed'>Select Camera</Text>
                        <select id="cameras"></select>
                    </Group>
                    :
                    null
                }

                <div style={{ flex: 1 }}>
                    <Stack spacing={0}>
                        <Text size='2rem' weight={700} style={{ paddingBottom: '0.5rem', lineHeight: 1.2 }}>{title}</Text>
                        <Text size='md' weight={500} color='dimmed'>{date}</Text>
                        {citations > 0 && (
                            <Text size='sm' color='dimmed' style={{ marginTop: '0.5rem' }}>{citations} Citations</Text>
                        )}
                        <Text size='md' style={{ marginTop: '1rem' }}>{description}</Text>
                        <Text size='md' style={{ margin: '1rem 0' }}>
                            {categories.map((item, index) => (
                                <Badge key={index} variant='outline' radius='xl' style={{ marginRight: '0.5rem' }}>
                                    {item}
                                </Badge>
                            ))}
                            {item.win &&
                                <Badge radius='xl' style={
                                    item.win.position === 0 ? { backgroundColor: '#FFB400', color: '#474747' } :
                                        item.win.position === 1 ? { backgroundColor: '#A4A4A4', color: '#F1F1F1' } :
                                            item.win.position === 2 ? { backgroundColor: '#8a5521ff', color: '#F1F1F1' } :
                                                { color: '#FFFFFF' }
                                }>
                                    {(item.win?.hackathon || item.win?.category) + ' Winner'}
                                </Badge>
                            }
                        </Text>

                        {lensID &&
                            // <Button component="a" href={`/snapar/${lensID}`} target='_blank' variant="filled" size='md' radius={'md'} style={{ marginTop: '0.5rem' }} leftIcon={<IconExternalLink />}>
                            //     Try on Browser
                            // </Button>
                            <Button onClick={() => { setStartAR(true); }} variant="filled" size='md' radius={'md'} style={{ marginTop: '0.5rem' }} leftIcon={<IconAugmentedReality />}>
                                Try on Browser
                            </Button>
                        }

                        {projectLink.map((item, index) => (
                            <Button key={index} component="a" href={item} target='_blank' variant="filled" size='md' radius={'md'} style={{ marginTop: '0.5rem' }} leftIcon={
                                item.includes('github.com') ? <GitHub /> :
                                    item.includes('devpost.com') ? <IconPaperclip /> :
                                        (item.includes('/pdf?') || item.includes('.pdf')) ? <IconFileTypePdf /> :
                                            item.includes('researchgate.net') ? <svg width="28px" height="28px" viewBox="0 0 28 28" fill='white' role="img"><path d="M19.586 0c-.818 0-1.508.19-2.073.565-.563.377-.97.936-1.213 1.68a3.193 3.193 0 0 0-.112.437 8.365 8.365 0 0 0-.078.53 9 9 0 0 0-.05.727c-.01.282-.013.621-.013 1.016a31.121 31.123 0 0 0 .014 1.017 9 9 0 0 0 .05.727 7.946 7.946 0 0 0 .077.53h-.005a3.334 3.334 0 0 0 .113.438c.245.743.65 1.303 1.214 1.68.565.376 1.256.564 2.075.564.8 0 1.536-.213 2.105-.603.57-.39.94-.916 1.175-1.65.076-.235.135-.558.177-.93a10.9 10.9 0 0 0 .043-1.207v-.82c0-.095-.047-.142-.14-.142h-3.064c-.094 0-.14.047-.14.141v.956c0 .094.046.14.14.14h1.666c.056 0 .084.03.084.086 0 .36 0 .62-.036.865-.038.244-.1.447-.147.606-.108.385-.348.664-.638.876-.29.212-.738.35-1.227.35-.545 0-.901-.15-1.21-.353-.306-.203-.517-.454-.67-.915a3.136 3.136 0 0 1-.147-.762 17.366 17.367 0 0 1-.034-.656c-.01-.26-.014-.572-.014-.939a26.401 26.403 0 0 1 .014-.938 15.821 15.822 0 0 1 .035-.656 3.19 3.19 0 0 1 .148-.76 1.89 1.89 0 0 1 .742-1.01c.344-.244.593-.352 1.137-.352.508 0 .815.096 1.144.303.33.207.528.492.764.925.047.094.111.118.198.07l1.044-.43c.075-.048.09-.115.042-.199a3.549 3.549 0 0 0-.466-.742 3 3 0 0 0-.679-.607 3.313 3.313 0 0 0-.903-.41A4.068 4.068 0 0 0 19.586 0zM8.217 5.836c-1.69 0-3.036.086-4.297.086-1.146 0-2.291 0-3.007-.029v.831l1.088.2c.744.144 1.174.488 1.174 2.264v11.288c0 1.777-.43 2.12-1.174 2.263l-1.088.2v.832c.773-.029 2.12-.086 3.465-.086 1.29 0 2.951.057 3.667.086v-.831l-1.49-.2c-.773-.115-1.174-.487-1.174-2.264v-4.784c.688.057 1.29.057 2.206.057 1.748 3.123 3.41 5.472 4.355 6.56.86 1.032 2.177 1.691 3.839 1.691.487 0 1.003-.086 1.318-.23v-.744c-1.031 0-2.063-.716-2.808-1.518-1.26-1.376-2.95-3.582-4.355-6.074 2.32-.545 4.04-2.722 4.04-4.9 0-3.208-2.492-4.698-5.758-4.698zm-.515 1.29c2.406 0 3.839 1.26 3.839 3.552 0 2.263-1.547 3.782-4.097 3.782-.974 0-1.404-.03-2.063-.086v-7.19c.66-.059 1.547-.059 2.32-.059z" /></svg> :
                                                item.includes('ieee.org') ? <svg  width="38px" height="38px" viewBox="0 0 32 30" fill='white' version="1.1" xmlns="http://www.w3.org/2000/svg"><path d="M11.005 12.785h1.836v6.624h-1.834zM13.804 12.785h5.045v1.285h-3.212v1.367h2.955v1.285h-2.955v1.403h3.212v1.285h-5.046zM19.878 12.785h5.045v1.285h-3.212v1.367h2.953v1.285h-2.953v1.403h3.21v1.285h-5.045zM25.95 12.785h5.046v1.285h-3.21v1.367h2.954v1.285h-2.955v1.403h3.212v1.285h-5.046zM6.028 13.031c0.281 0.214 0.53 0.426 0.766 0.651l-0.004-0.004 0.043 0.025c0.646 0.595 1.224 1.246 1.73 1.952l0.026 0.038c0.073 0.089 0.117 0.203 0.117 0.328 0 0.035-0.004 0.070-0.010 0.104l0.001-0.003c-0.222 0.425-0.488 0.79-0.799 1.109l0.001-0.001c-0.62 0.693-1.295 1.311-2.028 1.856l-0.036 0.026c-0.099 0.090-0.232 0.146-0.377 0.146-0.059 0-0.115-0.009-0.168-0.026l0.004 0.001c-0.571-0.354-1.065-0.75-1.507-1.199l-0.001-0.001c-0.577-0.524-1.089-1.103-1.532-1.733l-0.022-0.033c-0.051-0.079-0.081-0.176-0.081-0.279 0-0.013 0-0.026 0.001-0.038l-0 0.002c0.080-0.227 0.199-0.422 0.35-0.587l-0.001 0.001c0.539-0.663 1.11-1.254 1.729-1.794l0.017-0.015c0.018-0.022 0.039-0.041 0.062-0.058l0.001-0.001c0.269-0.232 0.535-0.437 0.815-0.639 0.292-0.228 0.661-0.039 0.907 0.171zM5.979 12.191l-0.332-0.47c-0.039-0.030-0.083-0.056-0.131-0.076l-0.004-0.002c-0.024-0.007-0.053-0.012-0.082-0.012-0.079 0-0.15 0.031-0.202 0.082l0-0-0.6 0.78c-0.964 1.207-2.064 2.248-3.291 3.122l-0.050 0.034c-0.099 0.076-0.244 0.151-0.275 0.277-0.004 0.018-0.006 0.039-0.006 0.060 0 0.088 0.040 0.167 0.103 0.219l0 0c1.049 0.726 1.964 1.524 2.781 2.413l0.011 0.012c0.15 0.159 0.271 0.315 0.419 0.462 0.247 0.331 0.537 0.662 0.766 1.016 0.071 0.075 0.082 0.197 0.195 0.234 0.041 0.019 0.089 0.030 0.139 0.030s0.098-0.011 0.141-0.031l-0.002 0.001 0.082-0.084c1.064-1.485 2.318-2.749 3.744-3.791l0.047-0.033c0.146-0.129 0.425-0.177 0.429-0.42-0.012-0.118-0.077-0.22-0.17-0.282l-0.001-0.001-0.025-0.004c-0.787-0.54-1.476-1.112-2.107-1.742l0 0-0.727-0.761q-0.444-0.503-0.855-1.033zM5.229 13.038c0.299-0.204 0.566 0.081 0.799 0.244 0.912 0.716 1.699 1.532 2.361 2.444l0.024 0.035c0.052 0.073 0.082 0.164 0.082 0.262 0 0.066-0.014 0.128-0.039 0.184l0.001-0.003c-0.196 0.297-0.395 0.555-0.612 0.798l0.006-0.007v0.014c-0.528 0.584-1.085 1.118-1.677 1.609l-0.026 0.021c-0.296 0.18-0.569 0.575-0.951 0.33-0.906-0.674-1.696-1.422-2.39-2.253l-0.018-0.022c-0.142-0.162-0.271-0.342-0.381-0.534l-0.009-0.016c-0.116-0.287 0.14-0.492 0.286-0.707 0.737-0.914 1.574-1.705 2.504-2.373l0.038-0.026zM5.417 13.624l-0.080 0.26-0.407 1.165c0.102 0.010 0.229 0 0.329 0.010v0.010l-0.060 1.317 0.010 0.012c0.061 0.008 0.131 0.012 0.202 0.012 0.078 0 0.156-0.005 0.231-0.015l-0.009 0.001v-0.021l-0.052-1.277 0.008-0.044 0.357-0.005q-0.268-0.709-0.517-1.425zM4.033 15.449c-0.227 0.114-0.562 0.287-0.526 0.6 0.076 0.163 0.21 0.287 0.375 0.349l0.005 0.002c0.449 0.183 0.97 0.288 1.516 0.288 0.436 0 0.856-0.068 1.25-0.193l-0.029 0.008c0.21-0.081 0.494-0.2 0.532-0.457-0.002-0.214-0.229-0.349-0.391-0.43v-0.010c0.052-0.022 0.113-0.039 0.176-0.048l0.004-0v-0.005c-0.285-0.051-0.562-0.126-0.838-0.2 0.054 0.121 0.091 0.25 0.136 0.376 0.074-0.025 0.161-0.046 0.251-0.058l0.008-0.001c0.146 0.056 0.355 0.137 0.375 0.32 0.017 0.171-0.17 0.255-0.287 0.325-0.331 0.121-0.714 0.19-1.113 0.19-0.31 0-0.61-0.042-0.895-0.121l0.024 0.006c-0.18-0.056-0.441-0.129-0.462-0.359 0.127-0.302 0.459-0.365 0.735-0.439-0.142-0.091-0.287-0.172-0.425-0.275-0.156 0.021-0.297 0.067-0.425 0.136l0.007-0.003zM5.191 16.762c-0.025 0.467-0.031 0.9-0.065 1.37 0.094 0.012 0.202 0.019 0.312 0.019 0.098 0 0.195-0.005 0.291-0.016l-0.012 0.001-0.059-1.307-0.011-0.063c-0.061 0.004-0.131 0.007-0.203 0.007-0.089 0-0.177-0.004-0.265-0.011l0.011 0.001zM6.608 19.744c0.005 0.161 0.137 0.29 0.299 0.29 0.165 0 0.299-0.134 0.299-0.299s-0.134-0.299-0.299-0.299c-0.082 0-0.157 0.033-0.211 0.087v0c-0.054 0.054-0.088 0.129-0.088 0.211 0 0.003 0 0.007 0 0.010v-0zM6.916 19.981c-0.005 0-0.010 0-0.016 0-0.131 0-0.237-0.106-0.237-0.237s0.106-0.237 0.237-0.237c0.13 0 0.236 0.105 0.237 0.235v0c0 0.004 0 0.008 0 0.012 0 0.123-0.099 0.223-0.221 0.225h-0zM7.054 19.678c0-0.075-0.050-0.099-0.136-0.099h-0.124v0.331h0.071v-0.139h0.034l0.076 0.139h0.085l-0.086-0.145c0.001 0 0.002 0 0.003 0 0.043 0 0.078-0.035 0.078-0.078 0-0.003-0-0.007-0.001-0.010l0 0zM6.928 19.715h-0.062v-0.080h0.050c0.027 0 0.064 0.003 0.064 0.037s-0.019 0.043-0.050 0.043z"></path></svg> :
                                                    item.includes('youtube.com') || item.includes('youtu.be') ? <IconBrandYoutube /> :
                                                        <IconExternalLink />
                            }>
                                {item.includes('github.com') ? 'GitHub' :
                                    item.includes('devpost.com') ? 'Devpost' :
                                        (item.includes('/pdf?') || item.includes('.pdf')) ? 'View PDF' :
                                            item.includes('researchgate.net') ? 'ResearchGate' :
                                                item.includes('ieee.org') ? 'IEEE' :
                                                    item.includes('youtube.com') || item.includes('youtu.be') ? <IconBrandYoutube /> :
                                                            item.includes('snapchat.com') ? 'Try on Snapchat' :
                                                                'Website'
                                }
                            </Button>
                        ))}
                        {item.more &&
                            <Button key={'rmd'} component="a" href={`/more/${item.id}`} target='' variant="filled" size='md' radius={'md'} style={{ marginTop: '0.5rem' }} leftIcon={
                                <IconArticle />
                            }>
                                Read More
                            </Button>
                        }
                    </Stack>
                </div>
            </div>
        </Modal >
    );
}

export default GalleryModal;